/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
* Goal: Summary of receivables
*/
import { TenantService } from '@src/app/shared/tenant.service';
import { DashboardChart } from '@src/app/shared/dashboard/dashboard-widget/dashboard-chart';


import * as Highcharts from 'highcharts';
import { uuid } from '@src/app/model/abstract-object';
import { DateHelper } from '@src/app/shared/dateHelper';
import { Cycle } from '@src/app/model/cycle';
import { NrHelper } from '@src/app/shared/NrHelper';
import { BudgetingPageNavRoute } from '@src/app/routes/app-router';
import { InvoicePageNavRoute } from '@src/app/routes/invoice-page-nav';
import { BankInPageNavRoute } from '@src/app/routes/bank-in-page-nav';

class ReceivableChartData {
    statusId: uuid;
    cycleId: uuid;
    yearStart: string;
    lastCollection: string;
    totalBudget: number;
    totalCharged: number;
    totalOutstanding: number;
    totalReceipts: number;
}

export class ReceivablesChart extends DashboardChart {
    dataUrl = TenantService.ajaxPath + 'omc/dashboard/receivables';
    //dataParams = new HttpParams().set('week', 0);

    data: ReceivableChartData = {
        statusId: Cycle.statusIds.ACCEPTED,
        cycleId: 'a015d499-711e-4c55-8f11-cd046842ddf6',
        yearStart: '2025-01-01',
        lastCollection: '2025-11-01',
        totalBudget: 100000,
        totalCharged: 103214.19,
        totalOutstanding: 20000,
        totalReceipts: 87000,
    }

    title = $localize `Receivables Analysis`;
    subTitle = $localize `Charges & Receipts Summary `;

    options = [];

    resetChart() {
        // Reset any counters for new data
    }

    getSubTitle() {
        return super.getSubTitle(this.subTitle);
    }

    protected setupData(data: ReceivableChartData) {
        this.data = data;
    }

    clickEvent($event) {
        const routeInfo = $event.point.routerLink;
        this.router.navigate(routeInfo);
    }

    billingColor() {
        if (this.data.totalCharged > this.data.totalBudget) {
            return this.greenColor;
        } else if (this.data.totalCharged > (this.data.totalBudget * .8)) {
            return this.amberColor 
        }
        return this.redColor; 
    }

    protected createChartOptions(): Highcharts.Options {
        const collectionDays = DateHelper.isoDaysBetween(this.data.yearStart, this.data.lastCollection);
        const daysToDate = DateHelper.isoDaysSince(this.data.yearStart)
        const projectedReceipts = (this.data.totalCharged / collectionDays * daysToDate);

        let budgetApproved = false;
        if (+this.data.statusId >= Cycle.statusIds.ACCEPTED) {
            budgetApproved = true;
        }

        const receiptsGood = (this.data.totalCharged - this.data.totalOutstanding) > projectedReceipts * .95;
        const receiptsOk = (this.data.totalCharged - this.data.totalOutstanding) > (projectedReceipts * .80);
        const receiptsColor = receiptsGood ? this.greenColor : receiptsOk ? this.amberColor : this.redColor

        const data = [
            // Target/Benchmark Data (e.g., Blue)
            { name: 'Total Budget', y: this.data.totalBudget, 
                color: budgetApproved ? this.blueColor : this.redColor, 
                description: budgetApproved ? $localize `Total budget as approved at AGM` : $localize `Provisional Budget NOT Approved`,
                routerLink: [BudgetingPageNavRoute.getIdUrl(this.data.cycleId)]
            },
            { name: 'Total Billed Out', y: this.data.totalCharged, color: this.billingColor(),
                description: $localize `Total charges to members in the current financial year`,
                routerLink: InvoicePageNavRoute.getListRouterLink({txnCycleId: this.data.cycleId})
            },
            { name: 'Total Outstanding', y: this.data.totalOutstanding, color: receiptsColor, 
                description: $localize `Total monies outstanding against this years service charges. This excludes monies owing from previous years. Shows in red/orange if greater then expected based on projected receipts`,
                routerLink: InvoicePageNavRoute.getListRouterLink({txnCycleId: this.data.cycleId, outstanding: '>=0.01'})
            },            
            { name: 'Total Credited', y: this.data.totalCharged - this.data.totalOutstanding, color: receiptsColor, 
                description: $localize `Total monies credited against this years charges, includes credit notes and bank receipts 
                    Shows in red/orange if less then projected`
            },
            { name: 'Total Receipts', y: this.data.totalReceipts, color: this.blueColor,
                description: $localize `Total actual cash receipts received this year, includes monies that where paid against previous arrears`,
                routerLink: BankInPageNavRoute.getListRouterLink({txnCycleId: this.data.cycleId, outstanding: '>=0.01'})               
            },
            { name: 'Projected Receipts', y: projectedReceipts, color: this.blueColor, 
                description: $localize `Target receipts to date, assuming all members pay regularly until ${DateHelper.longDate(this.data.lastCollection)}. This is not a real number, just a guideline to help decide if receipts are on track!`},

        ];        

        return {
            chart: {
                backgroundColor: this.chartBackgroundColor,
                borderRadius: this.chartBorderRadius,
                plotBorderWidth: null,
                type: 'column',
                height: this.height,
                width: this.width
            },
            title: this.getChartTitle(),
            subtitle: this.getSubTitle(),
            credits: { enabled: false },
            tooltip: {
                // Modified formatter to include the description from the point data
                formatter: function () {
                    const point = this.point;
                    let tooltipHtml = `<b>${point.name}</b><br/>Amount: ${NrHelper.toFormattedCurrency(point.y)}`;
                    // Access the custom 'description' property
                    if (point.options && point.options.description) {
                        tooltipHtml += `<br/><span class="text-xs text-gray-400">${point.options.description}</span>`;
                    }
                    return tooltipHtml;
                }
            },  
            xAxis: {
                categories: data.map(item => item.name),
                title: {
                    text: null
                },
                labels: {
                    style: {
                        fontSize: '11px',
                        color: '#6b7280'
                    }
                }                
            },
            yAxis: {
                min: 0,
                max: Math.max(this.data.totalBudget, this.data.totalCharged),
                title: {
                    text: $localize `Euros`,
                },
                labels: {
                    formatter: function () {
                        return NrHelper.toFormattedCurrency(this.value);
                    }
                },
                gridLineWidth: 1,
                gridLineColor: '#e5e7eb'
            },
            legend: { enabled: false },          
            plotOptions: {
                column: {
                    borderRadius: 5,
                    dataLabels: {
                        enabled: true,
                        crop: false,
                        overflow: 'none',
                        y: -10, // Position above the bar
                        formatter: function () {
                            // Show the number above the bar, formatted
                            //return NrHelper.toFormattedCurrency(this.y);
                        },
                        style: {
                            textOutline: 'none', 
                            fontSize: '12px'
                        }
                    },
                    pointPadding: 0.1,
                    groupPadding: 0.2,
                    point: {
                        events: {
                            click: this.gotoRoute.bind(this)
                        }
                    }
                }
            },
            series: [{
                name: 'Financial Metrics',
                data: data,
            }]
        } as unknown as Highcharts.Options;
    }
    
    gotoRoute($event: Highcharts.PointClickEventObject) {
        const point = $event.point as unknown as {routerLink: any[]};
        const routerLink = point.routerLink; 

        if (routerLink) {
            this.router.navigate(routerLink);
        }
    }
}
